import API_BASE_URL from "../config/api";
import { getVisitorId, trackEvent } from "./analytics";

export const PREMIUM_ACCESS_EVENT = "darbak:request-premium-access";
export const ACCOUNT_MODAL_EVENT = "darbak:open-account";
export const PREMIUM_STATUS_EVENT = "darbak:premium-status-changed";

const PREMIUM_PASS_KEY = "darbak_premium_pass_v1";
const PREMIUM_PREVIEW_KEY = "darbak_premium_gate_preview_v1";
const ACCESS_IDENTITY_KEY = "darbak_access_identity_v1";

const getPremiumPreviewFlag = () => {
  if (typeof window === "undefined") return false;

  try {
    const params = new URLSearchParams(window.location.search);
    const previewParam = params.get("premium_test");

    if (previewParam === "1") {
      window.localStorage.setItem(PREMIUM_PREVIEW_KEY, "true");
      return true;
    }

    if (previewParam === "0") {
      window.localStorage.removeItem(PREMIUM_PREVIEW_KEY);
      return false;
    }

    return window.localStorage.getItem(PREMIUM_PREVIEW_KEY) === "true";
  } catch {
    return false;
  }
};

export const isPremiumGateEnabled = () =>
  process.env.REACT_APP_PREMIUM_GATE_ENABLED === "true" ||
  getPremiumPreviewFlag();

export const getStoredPremiumPass = () => {
  if (typeof window === "undefined") return null;

  try {
    const stored = window.localStorage.getItem(PREMIUM_PASS_KEY);
    if (!stored) return null;

    const pass = JSON.parse(stored);
    const expiresAt = pass?.expiresAt ? new Date(pass.expiresAt) : null;

    if (!expiresAt || Number.isNaN(expiresAt.getTime()) || expiresAt <= new Date()) {
      window.localStorage.removeItem(PREMIUM_PASS_KEY);
      return null;
    }

    return pass;
  } catch {
    return null;
  }
};

export const hasActivePremiumPass = () => Boolean(getStoredPremiumPass());

export const passHasEntitlement = (pass = {}, entitlement = "") => {
  if (pass?.isAdmin) return true;
  const entitlements = Array.isArray(pass?.entitlements) ? pass.entitlements : [];
  if (
    entitlement === "darbak_plus" &&
    (pass?.accessType === "premium" || pass?.accessType === "admin")
  ) {
    return true;
  }
  return entitlements.includes(entitlement);
};

// Keep plan knowledge in one place. UI features should ask for a capability,
// never infer access from the selected checkout plan.
const PLAN_CAPABILITIES = {
  darbak_plus: { hasCoreAccess: true, hasResumeAccess: false },
  one_time_90: { hasCoreAccess: true, hasResumeAccess: false },
  darbak_resume: { hasCoreAccess: true, hasResumeAccess: true },
};

export const getSubscriptionCapabilities = (pass = getStoredPremiumPass()) => {
  if (pass?.isAdmin || pass?.accessType === "admin") {
    return { hasCoreAccess: true, hasResumeAccess: true };
  }
  if (pass?.status?.toLowerCase() === "expired" ||
    (pass?.expiresAt && new Date(pass.expiresAt) <= new Date())) {
    return { hasCoreAccess: false, hasResumeAccess: false };
  }

  const planCapabilities = PLAN_CAPABILITIES[pass?.planId] || {};
  return {
    hasCoreAccess:
      Boolean(planCapabilities.hasCoreAccess) ||
      passHasEntitlement(pass, "darbak_plus"),
    hasResumeAccess:
      Boolean(planCapabilities.hasResumeAccess) ||
      passHasEntitlement(pass, "resume_builder"),
  };
};

export const isCoreSubscriptionFeature = (detail = {}) => {
  const feature = (detail.feature || "").toString();
  const itemKey = (detail.itemKey || "").toString();
  return (
    feature.includes("experience") ||
    feature.includes("opportunity") ||
    feature.includes("where_to_train") ||
    feature.includes("training_guide") ||
    itemKey.startsWith("experience:") ||
    itemKey.startsWith("opportunity:") ||
    itemKey.startsWith("where-to-train:") ||
    itemKey.startsWith("where-to-train-opportunities:") ||
    itemKey.startsWith("guide-organization:")
  );
};

export const hasSubscriptionFeatureAccess = (
  detail = {},
  pass = getStoredPremiumPass()
) => {
  const capabilities = getSubscriptionCapabilities(pass);
  return detail.defaultPlanId === "darbak_resume"
    ? capabilities.hasResumeAccess
    : isCoreSubscriptionFeature(detail)
      ? capabilities.hasCoreAccess
      : false;
};

export const hasCoreAccess = (pass = getStoredPremiumPass()) =>
  getSubscriptionCapabilities(pass).hasCoreAccess;

export const hasResumeAccess = (pass = getStoredPremiumPass()) =>
  getSubscriptionCapabilities(pass).hasResumeAccess;

export const hasResumeAccessPass = () => hasResumeAccess();

export const hasDarbakPlusPass = () => hasCoreAccess();

export const getStoredAccessIdentity = () => {
  if (typeof window === "undefined") return {};

  try {
    return JSON.parse(window.localStorage.getItem(ACCESS_IDENTITY_KEY) || "{}");
  } catch {
    return {};
  }
};

export const getSafeSubscriptionReturnTo = (value = "") => {
  if (typeof value !== "string" || !value.startsWith("/") || value.startsWith("//")) return "";
  try {
    const url = new URL(value, "https://darbak.local");
    return url.origin === "https://darbak.local"
      ? `${url.pathname}${url.search}${url.hash}`
      : "";
  } catch {
    return "";
  }
};

export const getSubscriptionFlowPath = ({ planId = "", source = "", returnTo = "", campaignId = "" } = {}) => {
  const params = new URLSearchParams();
  if (planId) params.set("plan", planId);
  if (planId) params.set("step", "checkout");
  if (source) params.set("source", source);
  if (campaignId) params.set("campaignId", campaignId);
  const safeReturnTo = getSafeSubscriptionReturnTo(returnTo);
  if (safeReturnTo) params.set("returnTo", safeReturnTo);
  return `/subscribe${params.toString() ? `?${params}` : ""}`;
};

export const startSubscriptionFlow = (options = {}) => {
  if (typeof window === "undefined") return;
  const path = getSubscriptionFlowPath(options);
  trackEvent("subscription_cta_clicked", {
    metadata: {
      source: options.source || "unknown",
      planId: options.planId || "",
      campaignId: options.campaignId || "",
      authenticated: Boolean(getStoredAccessIdentity().accessCode),
      returnContext: Boolean(getSafeSubscriptionReturnTo(options.returnTo)),
    },
  });
  if (typeof options.navigate === "function") options.navigate(path);
  else window.location.assign(path);
};

export const saveAccessIdentity = ({ contact = "", email = "", accessCode = "" } = {}) => {
  if (typeof window === "undefined") return;

  const normalizedContact = (contact || email || "").toString().trim();
  const normalizedCode = accessCode.toString().trim();
  if (!normalizedContact || !normalizedCode) return;

  try {
    window.localStorage.setItem(
      ACCESS_IDENTITY_KEY,
      JSON.stringify({
        contact: normalizedContact,
        accessCode: normalizedCode,
        savedAt: new Date().toISOString(),
      })
    );
  } catch {
    // The user can still enter the same data again if storage is blocked.
  }
};

export const savePremiumPass = (pass) => {
  if (typeof window === "undefined") return;

  const storedPass = {
    contact: pass.contact || pass.email,
    email: pass.email,
    expiresAt: pass.expiresAt,
    isAdmin: Boolean(pass.isAdmin),
    accessType: pass.accessType || "premium",
    planId: pass.planId || "",
    planKey: pass.planKey || pass.planId || "",
    planLabel: pass.planLabel || "",
    entitlements: Array.isArray(pass.entitlements) ? pass.entitlements : [],
    hasResumeAccess: Boolean(pass.hasResumeAccess),
    aiResumeUsageCount: Number(pass.aiResumeUsageCount || 0),
    aiResumeUsageLimit: Number(pass.aiResumeUsageLimit || 0),
    aiResumeUsageResetAt: pass.aiResumeUsageResetAt || null,
    savedAt: new Date().toISOString(),
  };

  try {
    window.localStorage.setItem(PREMIUM_PASS_KEY, JSON.stringify(storedPass));
    window.dispatchEvent(
      new CustomEvent(PREMIUM_STATUS_EVENT, { detail: { pass: storedPass } })
    );
  } catch {
    // Access remains server-verifiable even if local storage is unavailable.
  }
};

export const clearAccessSession = () => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.removeItem(PREMIUM_PASS_KEY);
    window.localStorage.removeItem(ACCESS_IDENTITY_KEY);
    window.dispatchEvent(
      new CustomEvent(PREMIUM_STATUS_EVENT, { detail: { pass: null } })
    );
  } catch {
    // Logging out should stay best-effort if storage is blocked.
  }
};

export const getAccessPayload = (detail = {}) => {
  const identity = getStoredAccessIdentity();
  return {
    contact: identity.contact || identity.email || "",
    accessCode: identity.accessCode || "",
    visitorId: getVisitorId(),
    itemKey: detail.itemKey || "",
  };
};

export const getAccessHeaders = (detail = {}) => {
  const payload = getAccessPayload(detail);
  return Object.entries({
    "x-darbak-contact": payload.contact,
    "x-darbak-access-code": payload.accessCode,
    "x-darbak-visitor-id": payload.visitorId,
    "x-darbak-item-key": payload.itemKey,
    "x-darbak-access-gate": isPremiumGateEnabled() ? "true" : "",
  }).reduce((headers, [key, value]) => {
    if (value) headers[key] = value;
    return headers;
  }, {});
};

export const fetchSubscriptionState = async () => {
  const identity = getStoredAccessIdentity();
  if (!(identity.contact || identity.email) || !identity.accessCode) {
    return { state: "FREE", planId: "", expiresAt: null };
  }
  const response = await fetch(`${API_BASE_URL}/api/subscriptions/state`, {
    headers: getAccessHeaders(),
  });
  if (!response.ok) throw new Error("Subscription state unavailable");
  const state = await response.json();
  if (!["FREE", "ACTIVE", "EXPIRED"].includes(state?.state)) {
    throw new Error("Invalid subscription state");
  }
  return state;
};

const openPremiumGate = (detail, onGranted, accessStatus = {}) => {
  window.dispatchEvent(
    new CustomEvent(PREMIUM_ACCESS_EVENT, {
      detail: {
        ...detail,
        accessStatus,
        onGranted,
      },
    })
  );
};

export const requestPremiumAccess = (detail = {}, onGranted = () => {}) => {
  if (!isPremiumGateEnabled() || hasActivePremiumPass()) {
    onGranted();
    return true;
  }

  if (typeof window === "undefined") return false;

  fetch(`${API_BASE_URL}/api/access/check`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(getAccessPayload(detail)),
  })
    .then(async (response) => {
      const data = await response.json().catch(() => ({}));

      if (response.ok && data.granted) {
        if (data.isPremium || data.isAdmin) {
          savePremiumPass({
            ...data,
            contact: getStoredAccessIdentity().contact,
            email: getStoredAccessIdentity().contact,
            expiresAt:
              data.expiresAt ||
              new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          });
        }
        onGranted();
        return;
      }

      if (typeof detail.onLimited === "function") {
        detail.onLimited(data);
      }
      if (detail.deferGateOnLimited) {
        if (data?.reason === "daily_limit") {
          openPremiumGate({ ...detail, reminderOnly: true }, null, data);
        }
        return;
      }
      openPremiumGate(detail, onGranted, data);
    })
    .catch(() => {
      if (detail.deferGateOnLimited && typeof detail.onLimited === "function") {
        detail.onLimited({ reason: "check_failed" });
        return;
      }
      openPremiumGate(detail, onGranted, { reason: "check_failed" });
    });

  return false;
};
